import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedWord } from "./OurTeam";
import TrainingBenefitsInnovative from "../components/utilities/BenefitCard";
import TestimonialsSectionTwo from "../components/utilities/TestimonialsSectionTwo";
import { Helmet } from 'react-helmet';

const TrainingWorkshopsSEO = () => {
  const canonicalUrl = 'https://grehpraveshtherapy.com/training-workshops';

  return (
    <Helmet>
      {/* Standard Meta Tags */}
      <title>Professional Mental Health Training & Workshops | Greh Pravesh Therapy</title>
      <meta name="description" content="Specialized mental health training programs for professionals. Enhance your practice with expert-led workshops. Call 8591049845 to register." />

      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Social Media Tags */}
      <meta property="og:title" content="Professional Mental Health Training & Workshops" />
      <meta
        property="og:description"
        content="Expert-led workshops to enhance therapeutic skills and professional development for mental health practitioners."
      />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="/images/training.png" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Professional Mental Health Training & Workshops" />
      <meta
        name="twitter:description"
        content="Specialized training to help therapists grow their skills and improve patient care."
      />
      <meta name="twitter:image" content="/images/training.png" />

      {/* JSON-LD Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Mental Health Professional Development Workshops",
          "description": "Specialized training programs for therapists to enhance communication, stress management, and counseling skills",
          "provider": {
            "@type": "Organization",
            "name": "Greh Pravesh Therapy",
            "url": "https://grehpraveshtherapy.com"
          },
          "hasCourseInstance": [
            {
              "@type": "CourseInstance",
              "name": "Mindfulness & Stress Management Workshop",
              "courseMode": "online/in-person",
              "duration": "PT4H",
              "startDate": "2024-07-06",
              "endDate": "2024-07-27"
            },
            {
              "@type": "CourseInstance",
              "name": "Relationship Communication Intensive",
              "courseMode": "online/in-person",
              "duration": "PT12H",
              "startDate": "2024-07-04",
              "endDate": "2024-08-08"
            }
          ]
        })}
      </script>
    </Helmet>
  );
};


const WorkshopsStatus: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-xl shadow-md max-w-2xl mx-auto">
      <AnimatePresence>
        <motion.span
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-2xl font-bold text-orange-600 mb-4 text-center"
        >
          <motion.div variants={itemVariants}>
            No Upcoming Workshops Currently!!
          </motion.div>
        </motion.span>

        <motion.p
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-gray-700 text-center mb-4 leading-relaxed px-4"
        >
          <motion.div variants={itemVariants}>
            We develop specialized training workshops year-round for mental health professionals. Keep an eye out for our next transformative learning experience.
          </motion.div>
        </motion.p>

        <motion.span
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-sm italic text-gray-500 text-center"
        >
          <motion.div variants={itemVariants}>
            New workshops announced periodically
          </motion.div>
        </motion.span>
      </AnimatePresence>
    </div>
  );
};


const TrainingWorkShops: React.FC = () => {
  

  return (
    <>
      <TrainingWorkshopsSEO />
      <main className="flex-grow">
        <motion.div
          className='flex flex-col h-[120vh] md:flex-row relative overflow-hidden bg-gradient-to-br from-teal-800 to-teal-400'
        >
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-orange-800 to-orange-300 rounded-br-[100%] z-0"></div>
          <div className="absolute top-0 right-0 w-full h-full bg-contain bg-no-repeat bg-right-top opacity-10 md:opacity-20 z-0"></div>

          <motion.section
            className="w-full md:w-1/2 flex items-center justify-center px-6 py-12 mt-20 md:mt-0 md:py-24 relative z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="max-w-xl w-full md:ml-5 text-center md:text-left">
              <h1 className="font-fraunces-slab text-3xl md:text-5xl leading-tight mb-6 text-white">
                <span className="block text-2xl md:text-2xl mb-4 font-gentium-book-plus-regular text-white/70">
                  Professional Development for Therapists
                </span>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-6">
                  <motion.span
                    initial="hidden"
                    animate="visible"
                    variants={{
                      hidden: {},
                      visible: {
                        transition: {
                          staggerChildren: 0.8
                        }
                      }
                    }}
                    className="font-gentium-book-plus-regular text-white"
                  >
                    <AnimatedWord word="Elevate " />
                  </motion.span>
                  <motion.span
                    initial="hidden"
                    animate="visible"
                    variants={{
                      hidden: {},
                      visible: {
                        transition: {
                          staggerChildren: 0.8
                        }
                      }
                    }}
                    className="font-gentium-book-plus-regular text-white"
                  >
                    <AnimatedWord word="Your " />
                  </motion.span>
                  <motion.span
                    initial="hidden"
                    animate="visible"
                    variants={{
                      hidden: {},
                      visible: {
                        transition: {
                          staggerChildren: 0.8
                        }
                      }
                    }}
                    className="font-gentium-book-plus-regular text-white"
                  >
                    <AnimatedWord word="Practice" />
                  </motion.span>
                </div>

                <div className="space-y-4">
                  <p className="text-base md:text-xl text-white/90">
                    Specialized Training for Therapists and Mental Health Professionals
                  </p>
                  <p className="text-base md:text-lg text-white/80 italic">
                    Bridging Academic Knowledge with Practical Counseling Skills
                  </p>
                </div>
              </h1>

              <button className="bg-white text-teal-700  text-center px-6 py-3 text-md md:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all w-full md:w-auto mx-auto">
                Training Programs & Workshops
              </button>

            </div>
          </motion.section>

          <motion.section
            className="w-full md:w-1/2 h-screen relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
          >
            <img
              src="/images/training.png"
              alt="Personal Growth and Training"
              className="w-full h-full object-cover"
            />
          </motion.section>
        </motion.div>
        <TrainingBenefitsInnovative />
        <TestimonialsSectionTwo />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="container mx-auto px-4 py-8 max-w-6xl "
        >

          <motion.h1
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
            className="text-3xl md:text-4xl font-gentium-book-plus-regular font-bold mt-28 mb-12 text-center text-gray-800"
          >
            Workshops
          </motion.h1>
          <WorkshopsStatus />

          {/* <AnimatePresence>
          {workshops.map((workshop, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
              transition={{
                duration: 0.6,
                type: "spring",
                stiffness: 100,
                delay: index * 0.2
              }}
              className="mb-12 bg-white shadow-xl rounded-xl overflow-hidden border border-gray-100"
            >
              <div className="flex flex-col md:flex-row">
                {/* Image Section with Improved Responsiveness  
                <div className="md:w-1/3 relative overflow-hidden group">
                  <img
                    src={workshop.image}
                    alt={workshop.title}
                    className="w-full h-64 md:h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  {/* Enroll Button - Visible on Mobile and Hover  
                  <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center md:hidden">
                    <Button
                      className="w-3/4 md:hidden block"
                    >
                      ENROLL NOW
                    </Button>
                  </div>
                  {/* Hover Enroll Button for Desktop 
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    className="absolute inset-0 bg-black bg-opacity-20 items-center justify-center hidden md:flex"
                  >
                    <Button
                      className="w-3/4 hidden md:block"
                    >
                      ENROLL NOW
                    </Button>
                  </motion.div>
                </div>

                {/* Workshop Details 
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="md:w-2/3 p-6 md:p-8 space-y-6"
                >
                  <motion.h2
                    whileHover={{ scale: 1.02 }}
                    className="text-2xl md:text-3xl font-gentium-book-plus-regular font-semibold mb-4 text-gray-800"
                  >
                    {workshop.title}
                  </motion.h2>

                  <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                    {/* Duration  
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center bg-gray-50 p-3 md:p-4 rounded-lg"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6 mr-3 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <strong className="block text-gray-700 text-sm md:text-base">Duration</strong>
                        <p className="text-gray-600 text-xs md:text-sm">{workshop.duration}</p>
                      </div>
                    </motion.div>

                    {/* Schedule  
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center bg-gray-50 p-3 md:p-4 rounded-lg"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6 mr-3 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <strong className="block text-gray-700 text-sm md:text-base">Schedule</strong>
                        <p className="text-gray-600 text-xs md:text-sm">{workshop.schedule}</p>
                      </div>
                    </motion.div>

                    {/* Price  
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center bg-gray-50 p-3 md:p-4 rounded-lg"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6 mr-3 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <strong className="block text-gray-700 text-sm md:text-base">Price</strong>
                        <p className="text-gray-600 text-xs md:text-sm">{workshop.price}</p>
                      </div>
                    </motion.div>

                    {/* Criteria  
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="bg-gray-50 p-3 md:p-4 rounded-lg"
                    >
                      <div className="flex items-center mb-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6 mr-3 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        <strong className="text-gray-700 text-sm md:text-base">Participant Criteria</strong>
                      </div>
                      <ul className="list-disc pl-6 text-gray-600 space-y-1 text-xs md:text-sm">
                        {workshop.criteria.map((criterion, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                          >
                            {criterion}
                          </motion.li>
                        ))}
                      </ul>
                    </motion.div>

                    {/* Benefits  
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="bg-gray-50 p-3 md:p-4 rounded-lg"
                    >
                      <div className="flex items-center mb-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6 mr-3 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                        <strong className="text-gray-700 text-sm md:text-base">Workshop Benefits</strong>
                      </div>
                      <ul className="list-disc pl-6 text-gray-600 space-y-1 text-xs md:text-sm">
                        {workshop.benefits.map((benefit, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                          >
                            {benefit}
                          </motion.li>
                        ))}
                      </ul>
                    </motion.div>
                  </div>

                  {/* Mobile Enroll Button  
                  <div className="md:hidden mt-6 text-center">
                    <Button className="w-full">
                      ENROLL NOW
                    </Button>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence> */}
        </motion.div>
      </main>
    </>
  );
};

export default TrainingWorkShops;